import { useMemo } from 'react';
import {
  CriteriaCountByState,
  Criterion,
  Field,
  NewQuota,
  ParticipantField,
  SelectChoiceField,
  useForm,
  useRecruitment,
  useParticipantFieldsByName,
} from '@app/api';
import { stringityItem, useGetCriterionValueText } from 'lib/criteriaUtils';
import { getQuestionNumber } from 'components/CustomForm/FormBuilder/lib/getQuestionNumber';
import { getColorForElement } from 'pages/Dashboard/EditSession/components/Quota/useQuotasHooks';

interface UseCriteriaOptions {
  criteria: Criterion[];
  participantFields: Record<string, ParticipantField>;
  formFields?: Field[] | undefined;
  quotas?: NewQuota[];
  criteriaCountByState: any;
}

export const getConfirmedParticipation = (
  criteriaCountByState: CriteriaCountByState,
  criterionId: string,
  quotaItem: string,
): number => {
  const confirmed =
    criteriaCountByState?.[criterionId]?.[quotaItem]?.['CONFIRMED'] || 0;
  const planned =
    criteriaCountByState?.[criterionId]?.[quotaItem]?.['PLANNED'] || 0;

  return confirmed + planned;
};

export function useProcessedCriteria({
  criteria,
  participantFields,
  formFields,
  quotas,
  criteriaCountByState,
}: UseCriteriaOptions) {
  const getCriterionValueText = useGetCriterionValueText();
  return useMemo(() => {
    const processedCriteria = [];
    for (let index = 0; index < criteria.length; index++) {
      const criterion = criteria[index];
      if (!criterion.screenerCriterion) {
        const field = participantFields[criterion.fieldRef];
        if (!field) continue;
        const quotaItems = criterion.quotas?.map((quota) => {
          const itemStringify = stringityItem(quota.item);
          const displayableLabel = getCriterionValueText(quota.item, field);

          return {
            ...quota,
            label: displayableLabel,
            confirmed: getConfirmedParticipation(
              criteriaCountByState,
              criterion.id,
              itemStringify,
            ),
          };
        });

        processedCriteria.push({
          ...criterion,
          fieldLabel: field.range?.label || field.label || '',
          quotas: quotaItems,
          color: getColorForElement(criterion.id),
        });
        continue;
      }

      const formField = formFields?.find(
        (field) => field.ref === criterion.fieldRef,
      ) as SelectChoiceField | undefined;

      if (!formField) continue;
      const questionNumber = getQuestionNumber(formFields || [], formField);

      const criterionQuotas = [
        ...(criterion?.quotas || []),
        ...(quotas?.filter((q) => q.ref === criterion.fieldRef) || []),
      ];

      const quotaItems = criterionQuotas.map((quota) => {
        const choice = formField?.properties.choices?.find(
          (choice) => choice.ref === quota.item,
        );
        return {
          ...quota,
          label: choice ? choice.label : '',
          confirmed:
            quota.confirmed ??
            getConfirmedParticipation(
              criteriaCountByState,
              criterion.id,
              quota.item.toString(),
            ),
        };
      });

      processedCriteria.push({
        ...criterion,
        fieldLabel: `Q${questionNumber} - ` + (formField?.title || ''),
        quotas: quotaItems,
        color: getColorForElement(criterion.id),
      });
    }
    return processedCriteria;
  }, [
    criteria,
    formFields,
    quotas,
    participantFields,
    getCriterionValueText,
    criteriaCountByState,
  ]);
}

export function useEnrichedQuotas(
  recruitmentId: string | undefined,
  quotas: NewQuota[] | undefined,
): NewQuota[] {
  const { data: recruitment } = useRecruitment(recruitmentId);
  const { data: form } = useForm(recruitment?.screeningFormId);
  const participantFields = useParticipantFieldsByName();
  const getCriterionValueText = useGetCriterionValueText();

  return useMemo(() => {
    if (!quotas || quotas.length === 0) {
      return [];
    }

    return quotas.map((quota) => {
      const criterion = recruitment?.criteria?.find(
        (c) => c.fieldRef === quota.ref,
      );

      if (!criterion) {
        return quota;
      }

      if (!criterion.screenerCriterion) {
        const field = participantFields[criterion.fieldRef];
        if (!field) return quota;

        const displayableLabel = getCriterionValueText(quota.item, field);
        return {
          ...quota,
          label: displayableLabel,
        };
      }

      const formField = form?.fields?.find(
        (field) => field.ref === criterion.fieldRef,
      ) as SelectChoiceField | undefined;

      if (!formField) return quota;

      const choice = formField?.properties.choices?.find(
        (choice) => choice.ref === quota.item,
      );

      return {
        ...quota,
        label: choice ? choice.label : quota.label || '',
      };
    });
  }, [
    quotas,
    recruitment,
    form?.fields,
    participantFields,
    getCriterionValueText,
  ]);
}
